import { useEffect, useRef } from "react";

/**
 * Adds `.is-visible` to children with `.reveal` once they enter the viewport.
 * Restrained: only triggers once per element.
 */
export default function useReveal(root) {
  const ref = useRef(null);

  useEffect(() => {
    const scope = root?.current || ref.current || document;
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (reduced) {
      const revealAll = () => {
        scope
          .querySelectorAll(".reveal")
          .forEach((n) => n.classList.add("is-visible"));
      };

      revealAll();

      const observer = new MutationObserver(revealAll);
      observer.observe(scope, { childList: true, subtree: true });
      return () => observer.disconnect();
    }

    const observed = new WeakSet();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    const observeRevealNodes = () => {
      scope.querySelectorAll(".reveal:not(.is-visible)").forEach((node) => {
        if (observed.has(node)) return;
        observed.add(node);
        io.observe(node);
      });
    };

    observeRevealNodes();

    const observer = new MutationObserver(observeRevealNodes);
    observer.observe(scope, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      io.disconnect();
    };
  }, [root]);

  return ref;
}
